/**
 * Detect which AI coding agents are installed on the user's machine.
 *
 * Returns a list of agents with their config paths so the installer
 * knows where to write hook configs.
 */
import { existsSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { execFileSync, execSync } from 'node:child_process';

export type AgentKind = 'claude_code' | 'cursor';

export interface DetectedAgent {
  kind: AgentKind;
  name: string;
  binaryPath?: string;
  configDir: string;
  settingsPath: string;
  version?: string;
}

function which(cmd: string): string | undefined {
  try {
    const result = execSync(`which ${cmd}`, { encoding: 'utf-8' }).trim();
    return result || undefined;
  } catch {
    return undefined;
  }
}

function getVersion(cmd: string): string | undefined {
  try {
    const result = execFileSync(cmd, ['--version'], { encoding: 'utf-8', timeout: 5000 }).trim();
    const line = result.split('\n')[0];
    return line.replace(/\s*\(Claude Code\)/, '');
  } catch {
    return undefined;
  }
}

export function detectAgents(): DetectedAgent[] {
  const agents: DetectedAgent[] = [];
  const home = homedir();

  // Claude Code — require the binary on PATH; ~/.claude/ alone is not enough
  // (Synkro and other tools create that directory).
  const claudeBinary = which('claude');
  const claudeConfigDir = join(home, '.claude');
  if (claudeBinary) {
    agents.push({
      kind: 'claude_code',
      name: 'Claude Code',
      binaryPath: claudeBinary,
      configDir: claudeConfigDir,
      settingsPath: join(claudeConfigDir, 'settings.json'),
      version: getVersion('claude'),
    });
  }

  // Cursor — GUI app, rarely has a `cursor` binary on PATH. Check the app
  // bundle (/Applications/Cursor.app on macOS) and ~/.cursor/ (created by
  // Cursor on first launch) as additional signals.
  const cursorBinary = which('cursor');
  const cursorConfigDir = join(home, '.cursor');
  const cursorApp = process.platform === 'darwin' && existsSync('/Applications/Cursor.app');
  if (cursorBinary || cursorApp || existsSync(cursorConfigDir)) {
    let version: string | undefined;
    if (cursorBinary) {
      version = getVersion('cursor');
    } else if (cursorApp) {
      try {
        const plist = execSync('defaults read /Applications/Cursor.app/Contents/Info CFBundleShortVersionString 2>/dev/null', { encoding: 'utf-8', timeout: 3000 }).trim();
        if (plist) version = plist;
      } catch {}
    }
    agents.push({
      kind: 'cursor',
      name: 'Cursor',
      binaryPath: cursorBinary,
      configDir: cursorConfigDir,
      settingsPath: join(cursorConfigDir, 'hooks.json'),
      version,
    });
  }

  return agents;
}

export function findClaudeAuth(): { path: string; exists: boolean } {
  // CC stores OAuth token in macOS keychain (item: "Claude Code-credentials")
  // or in ~/.claude/auth.json on Linux. We don't extract it; we ask user to
  // run `claude setup-token` for headless use.
  const authJsonPath = join(homedir(), '.claude', 'auth.json');
  return { path: authJsonPath, exists: existsSync(authJsonPath) };
}
